import { useState, useEffect, useCallback } from "react";
import { adminApi } from "../lib/api";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationPrevious,
  PaginationNext,
  PaginationEllipsis,
} from "../components/ui/pagination";
import {
  formatCurrency,
  formatDateTime,
  getStatusColor,
  truncate,
} from "../lib/utils";
import { toast } from "sonner";
import {
  Search,
  Eye,
  Truck,
  Loader2,
  Package,
  MapPin,
  User,
  Clock,
  ExternalLink,
} from "lucide-react";

const ORDER_STATUSES = [
  { value: "pending", label: "Pending" },
  { value: "confirmed", label: "Confirmed" },
  { value: "processing", label: "Processing" },
  { value: "shipped", label: "Shipped" },
  { value: "delivered", label: "Delivered" },
  { value: "cancelled", label: "Cancelled" },
  { value: "returned", label: "Returned" },
];

export default function OrdersPage() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [statusForm, setStatusForm] = useState({
    status: "",
    tracking_link: "",
    tracking_number: "",
    notes: "",
    timestamp: "",
  });
  const [updating, setUpdating] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalOrders, setTotalOrders] = useState(0);
  const pageSize = 20;

  useEffect(() => {
    fetchOrders();
  }, [statusFilter, currentPage]);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const params = {
        limit: pageSize,
        skip: (currentPage - 1) * pageSize,
      };
      if (statusFilter && statusFilter !== "all") params.status = statusFilter;
      if (search) params.search = search;
      const res = await adminApi.getOrders(params);
      setOrders(res.data);
    } catch (error) {
      toast.error("Failed to load orders");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchOrders();
  };

  const openOrderDetails = (order) => {
    setSelectedOrder(order);
    setShowDetails(true);
  };

  const openStatusUpdate = (order) => {
    setSelectedOrder(order);
    setStatusForm({
      status: order.status,
      tracking_link: order.tracking_link || "",
      tracking_number: order.tracking_number || "",
      notes: "",
    });
    setShowStatusModal(true);
  };

  const handleStatusUpdate = async () => {
    if (!statusForm.status) {
      toast.error("Please select a status");
      return;
    }

    setUpdating(true);
    try {
      // Always send current datetime as ISO string
      const payload = {
        ...statusForm,
        timestamp: new Date().toISOString(),
      };
      await adminApi.updateOrderStatus(selectedOrder.id, payload);
      toast.success("Order status updated");
      setShowStatusModal(false);
      fetchOrders();
    } catch (error) {
      toast.error("Failed to update status");
    } finally {
      setUpdating(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="orders-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl text-primary font-semibold">
            Orders
          </h1>
          <p className="text-muted-foreground mt-1">Manage customer orders</p>
        </div>
      </div>

      {/* Filters */}
      <Card className="card-base">
        <CardContent className="p-4">
          <form onSubmit={handleSearch} className="flex gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by order number, customer..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 input-base"
                data-testid="search-orders-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40 input-base">
                <SelectValue placeholder="All statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {ORDER_STATUSES.map((s) => (
                  <SelectItem key={s.value} value={s.value}>
                    {s.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              type="submit"
              variant="outline"
              className="border-primary text-primary"
            >
              Search
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Orders Table */}
      <Card className="card-base">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="table-header">
                  <TableHead>Order</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Items</TableHead>
                  <TableHead>Total</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {orders.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={7}
                      className="text-center py-8 text-muted-foreground"
                    >
                      No orders found
                    </TableCell>
                  </TableRow>
                ) : (
                  orders.map((order) => (
                    <TableRow key={order.id} className="hover:bg-canvas/50">
                      <TableCell>
                        <span className="font-mono text-sm font-medium text-primary">
                          {order.order_number}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium text-primary">
                            {order.customer_name}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {order.customer_email}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-muted-foreground">
                          {order.items?.length || 0} items
                        </span>
                      </TableCell>
                      <TableCell className="font-semibold">
                        {formatCurrency(order.total)}
                      </TableCell>
                      <TableCell>
                        <Badge
                          className={`${getStatusColor(order.status)} border`}
                        >
                          {order.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm">
                        {formatDateTime(order.created_at)}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openOrderDetails(order)}
                            data-testid={`view-order-${order.id}`}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openStatusUpdate(order)}
                            data-testid={`update-status-${order.id}`}
                          >
                            <Truck className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {!loading && orders.length > 0 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Showing {(currentPage - 1) * pageSize + 1}-
            {Math.min(currentPage * pageSize, totalOrders)} of ~{totalOrders}{" "}
            orders
          </p>
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    if (currentPage > 1) setCurrentPage(currentPage - 1);
                  }}
                  className={currentPage === 1 ? "pointer-events-none opacity-50" : ""}
                />
              </PaginationItem>
              <PaginationItem>
                <PaginationLink
                  href="#"
                  isActive={true}
                >
                  {currentPage}
                </PaginationLink>
              </PaginationItem>
              <PaginationItem>
                <PaginationNext
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    if (orders.length === pageSize) setCurrentPage(currentPage + 1);
                  }}
                  className={orders.length < pageSize ? "pointer-events-none opacity-50" : ""}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      )}

      {/* Order Details Modal */}
      <Dialog open={showDetails} onOpenChange={setShowDetails}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-primary flex items-center gap-2">
              <Package className="w-5 h-5" />
              Order {selectedOrder?.order_number}
            </DialogTitle>
          </DialogHeader>

          {selectedOrder && (
            <div className="space-y-6 py-4">
              {/* Status & Tracking */}
              <div className="flex items-center justify-between p-4 bg-canvas rounded-lg">
                <div>
                  <Badge
                    className={`${getStatusColor(selectedOrder.status)} border text-sm`}
                  >
                    {selectedOrder.status.toUpperCase()}
                  </Badge>
                  <p className="text-xs text-muted-foreground mt-1">
                    Last updated: {formatDateTime(selectedOrder.updated_at)}
                  </p>
                </div>
                {selectedOrder.tracking_link && (
                  <a
                    href={selectedOrder.tracking_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-primary hover:underline text-sm"
                  >
                    Track Order <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>

              {/* Customer Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 border border-[#E7E5E4] rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <User className="w-4 h-4 text-primary" />
                    <span className="font-medium text-primary">Customer</span>
                  </div>
                  <p className="text-sm">{selectedOrder.customer_name}</p>
                  <p className="text-sm text-muted-foreground">
                    {selectedOrder.customer_email}
                  </p>
                </div>
                <div className="p-4 border border-[#E7E5E4] rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <MapPin className="w-4 h-4 text-primary" />
                    <span className="font-medium text-primary">
                      Shipping Address
                    </span>
                  </div>
                  {selectedOrder.shipping_address?.fullName && (
                    <p className="text-sm font-medium text-primary">
                      {selectedOrder.shipping_address.fullName}
                    </p>
                  )}
                  {selectedOrder.shipping_address?.phone && (
                    <p className="text-sm text-muted-foreground">
                      +91 {selectedOrder.shipping_address.phone}
                    </p>
                  )}
                  <p className="text-sm mt-2">
                    {selectedOrder.shipping_address?.street ||
                      selectedOrder.shipping_address?.address}
                    <br />
                    {selectedOrder.shipping_address?.city},{" "}
                    {selectedOrder.shipping_address?.state}
                    <br />
                    {selectedOrder.shipping_address?.pincode}
                  </p>
                </div>
              </div>

              {/* Order Items */}
              <div>
                <h3 className="font-medium text-primary mb-3">Items</h3>
                <div className="space-y-3">
                  {selectedOrder.items?.map((item, idx) => (
                    <div
                      key={idx}
                      className="flex items-center justify-between p-3 border border-[#E7E5E4] rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        {item.image ? (
                          <img
                            src={`${item.image}`}
                            alt={item.product_name}
                            className="w-12 h-12 object-cover rounded"
                          />
                        ) : (
                          <div className="w-12 h-12 bg-muted rounded flex items-center justify-center">
                            <Package className="w-5 h-5 text-muted-foreground" />
                          </div>
                        )}
                        <div>
                          <p className="font-medium text-primary">
                            {item.product_name}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            SKU: {item.sku || "N/A"} | Color: {item.color_name} | Qty: {item.quantity}
                          </p>
                        </div>
                      </div>
                      <p className="font-semibold">
                        {formatCurrency(item.price * item.quantity)}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Order Summary */}
              <div className="p-4 bg-canvas rounded-lg">
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Subtotal</span>
                    <span>{formatCurrency(selectedOrder.subtotal)}</span>
                  </div>
                  {selectedOrder.discount > 0 && (
                    <div className="flex justify-between text-sm text-green-600">
                      <span>
                        Discount{" "}
                        {selectedOrder.coupon_code &&
                          `(${selectedOrder.coupon_code})`}
                      </span>
                      <span>-{formatCurrency(selectedOrder.discount)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Shipping</span>
                    <span>{formatCurrency(selectedOrder.shipping)}</span>
                  </div>
                  <div className="flex justify-between font-semibold text-primary pt-2 border-t">
                    <span>Total</span>
                    <span>{formatCurrency(selectedOrder.total)}</span>
                  </div>
                </div>
              </div>

              {/* Status History */}
              {selectedOrder.status_history?.length > 0 && (
                <div>
                  <h3 className="font-medium text-primary mb-3 flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    Status History
                  </h3>
                  <div className="space-y-2">
                    {selectedOrder.status_history.map((h, idx) => (
                      <div
                        key={idx}
                        className="flex items-center gap-3 text-sm"
                      >
                        <Badge
                          className={`${getStatusColor(h.status)} border text-xs`}
                        >
                          {h.status}
                        </Badge>
                        <span className="text-muted-foreground">
                          {formatDateTime(h.timestamp)}
                        </span>
                        {h.notes && (
                          <span className="text-muted-foreground">
                            - {h.notes}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDetails(false)}>
              Close
            </Button>
            <Button
              onClick={() => {
                setShowDetails(false);
                openStatusUpdate(selectedOrder);
              }}
              className="bg-primary text-white hover:bg-primary/90"
            >
              Update Status
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Update Status Modal */}
      <Dialog open={showStatusModal} onOpenChange={setShowStatusModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-primary">
              Update Order Status
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label className="text-primary font-medium">Status</Label>
              <Select
                value={statusForm.status}
                onValueChange={(v) =>
                  setStatusForm({ ...statusForm, status: v })
                }
              >
                <SelectTrigger
                  className="input-base mt-1"
                  data-testid="status-select"
                >
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  {ORDER_STATUSES.map((s) => (
                    <SelectItem key={s.value} value={s.value}>
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {statusForm.status === "shipped" && (
              <>
                <div>
                  <Label className="text-primary font-medium">
                    Tracking Number
                  </Label>
                  <Input
                    value={statusForm.tracking_number}
                    onChange={(e) =>
                      setStatusForm({
                        ...statusForm,
                        tracking_number: e.target.value,
                      })
                    }
                    placeholder="Enter tracking number"
                    className="input-base mt-1"
                  />
                </div>
                <div>
                  <Label className="text-primary font-medium">
                    Tracking Link
                  </Label>
                  <Input
                    value={statusForm.tracking_link}
                    onChange={(e) =>
                      setStatusForm({
                        ...statusForm,
                        tracking_link: e.target.value,
                      })
                    }
                    placeholder="https://tracking.example.com/..."
                    className="input-base mt-1"
                  />
                </div>
              </>
            )}

            <div>
              <Label className="text-primary font-medium">
                Notes (Optional)
              </Label>
              <Textarea
                value={statusForm.notes}
                onChange={(e) =>
                  setStatusForm({ ...statusForm, notes: e.target.value })
                }
                placeholder="Add any notes..."
                className="input-base mt-1"
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowStatusModal(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleStatusUpdate}
              disabled={updating}
              className="bg-primary text-white hover:bg-primary/90"
              data-testid="update-status-btn"
            >
              {updating && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Update Status
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
